import { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, GiftIcon, XIcon } from 'lucide-react';
import { useExitIntent } from '../hooks/useExitIntent';
import { prefetchPage } from '../utils/prefetch';
const SPECIAL_OFFER_URL = 'https://megapack-funzionale-365.impultienda.ar/oferta-especial?store_id=17769399-b033-4e94-b8c9-56f445b8f2ca';
export function ExitIntentModal() {
  const {
    closeOffer,
    isOpen
  } = useExitIntent();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    prefetchPage(SPECIAL_OFFER_URL);
    closeButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeOffer();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusableElements = Array.from(dialogRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled])'));
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (!firstElement || !lastElement) return;
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [closeOffer, isOpen]);
  return <AnimatePresence data-id="element-158" data-mp="152zxi9d7wgc3">
      {isOpen && <motion.div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: 'easeOut'
    }} role="presentation" onClick={e => {
      if (e.target === e.currentTarget) closeOffer();
    }} data-id="element-159" data-mp="151yp6cnl67y2">
          <motion.div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="exit-offer-title" aria-describedby="exit-offer-description" className="relative w-full max-w-md overflow-hidden rounded-3xl border border-sky-400/30 bg-slate-900 p-6 text-center shadow-2xl shadow-blue-950/60 md:p-8" initial={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 12
      }} transition={{
        type: 'spring',
        stiffness: 360,
        damping: 30
      }} data-id="element-160" data-mp="1j5yajwg43eai">
            <button ref={closeButtonRef} type="button" onClick={closeOffer} className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/5 text-slate-400 transition-colors duration-150 hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400" aria-label="Chiudi l'offerta esclusiva" data-id="element-161" data-mp="bqa3ku5luyiv">
              <XIcon size={18} aria-hidden="true" data-id="element-162" data-mp="3vbhbhm2kqein" />
            </button>

            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-600 text-white" data-id="element-163" data-mp="3raf94wdd1mcg">
              <GiftIcon size={26} aria-hidden="true" data-id="element-164" data-mp="3mdym65hwde0y" />
            </div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-sky-400" data-id="element-165" data-mp="3hldsd3yzr0xf">
              Offerta esclusiva · solo adesso
            </p>
            <h2 id="exit-offer-title" className="mb-4 font-display text-3xl font-black italic uppercase leading-[0.95] tracking-tight text-white md:text-4xl" data-id="element-166" data-mp="3ck801798tfof">
              Aspetta! Abbiamo un regalo per te
            </h2>
            <p id="exit-offer-description" className="mx-auto mb-7 max-w-sm text-sm leading-relaxed text-slate-300 md:text-base" data-id="element-167" data-mp="2yo4ctxwse7lg">
              Prima di andare, guarda questa offerta esclusiva con uno sconto
              disponibile solo adesso. Paghi comodamente in euro.
            </p>

            <div className="flex flex-col gap-2.5" data-id="element-168" data-mp="ss0ugtqpjoon">
              <a href={SPECIAL_OFFER_URL} rel="noopener" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-blue-600 px-6 py-4 text-base font-bold uppercase tracking-wide text-white shadow-[0_10px_30px_-10px_rgba(37,99,235,0.8)] transition-[transform,background-color] duration-200 ease-out hover:-translate-y-0.5 hover:bg-blue-500 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900" data-id="element-169" data-mp="xrwpw4lca7sl">
                Vedi l'offerta <ArrowRightIcon size={18} aria-hidden="true" data-id="element-170" data-mp="29724a5uu1us1" />
              </a>
              <button type="button" onClick={closeOffer} className="inline-flex w-full items-center justify-center rounded-full px-6 py-3 text-sm font-medium text-slate-400 transition-colors duration-150 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400" data-id="element-171" data-mp="2jbiikusnz3tq">
                No, grazie
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}